import React, { useCallback, useMemo, forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { gridLayoutActions } from "../../redux/reducers/gridLayoutReducer";

import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import "ag-grid-enterprise";
import { LicenseManager } from "ag-grid-enterprise";

import { SFGridContainerItem, SFGridItem, SFStack } from "../grid";
import useStyled from "../../hooks/useStyled";
import { Logger } from "@base-provider";
import { SFIconButton } from "../buttons";
import { GRID_OPTIONS } from "./dataGridConfig";
import RowContainer from "./gridRowView";
import { clientSideFilterActions } from "../../redux/reducers/clientSideFilterReducer";

import "./grid.scss";

import _ from "lodash";

if (process.env?.REACT_APP_AG_GRID_LICENSE_KEY) {
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY);
}

const { styled } = useStyled();

const SFStackStyled = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "borderBottom" && prop !== "hideGrid",
})(({ theme, borderBottom, hideGrid }) => ({
  paddingBottom: theme.spacing(1),
  ...(!hideGrid && {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1) + "!important",
    backgroundColor: theme.palette.grey550.main,
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
    borderBottom: borderBottom ? "1px solid rgba(0, 0, 0, 0.9)" : "",
  }),
}));

const StyledGridWrapper = styled("div", { shouldForwardProp: false })(
  ({ theme, noCardBorder, isopen }) => ({
    height: "100%",
    width: "100%",
    border: noCardBorder ? "none" : "1px solid rgba(0, 0, 0, 0.9)",
    borderRadius: noCardBorder ? 0 : 4,
    display: "flex",
    flexDirection: "column",
    "& .ag-theme-ey": {
      zIndex: 1000,
      display: "block",
    },
  })
);

const SFDataGridCore = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const savedLayout = useSelector((state) => {
    return state.gridLayout.find(
      (gridLayout) => gridLayout.gridName === props.gridName
    );
  });

  const [loading, setLoading] = useState(null);

  const dispatch = useDispatch();

  //setting up default column definitions
  const defaultColDefs = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: false,
      filterParams: {
        suppressSelectAll: true,
        defaultToNothingSelected: true,
        refreshValuesOnOpen: true,
        values: props.getValues,
      },
      //hides any menus shown next to column headers
      suppressMenu: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      suppressHeaderContextMenu: true,
    };
  }, []);

  const handleGridReady = useCallback((params) => {
    let newFilterState = combineFilterState(
      props.initialGridState,
      savedLayout?.state
    );

    params.api.setFilterModel(newFilterState);

    if (props.getServerSideDataSource) {
      setTimeout(() => {
        let datasource = props.getServerSideDataSource();
        params.api.setGridOption(
          GRID_OPTIONS.SERVER_SIDE_DATASOURCE,
          datasource
        );
      }, 1000);
    }
    setLoading(false);
    Logger.of("App.Core.DataGrid.handleGridReady").info({
      params,
      hideGrid: props.hideGrid,
      props,
    });
    if (props.isClientGrid)
      params.api.setGridOption(GRID_OPTIONS.DOM_LAYOUT, "autoHeight");
  }, []);

  const onStateUpdated = (params) => {
    if (props.persistState) {
      if (props.isClientGrid) {
        let _state = _.cloneDeep(params.state.filter);
        dispatch(
          clientSideFilterActions.saveState({
            gridName: props.gridName,
            state: { filter: _state },
          })
        );
      } else {
        let _state = _.cloneDeep(params.state);
        dispatch(
          gridLayoutActions.saveState({
            gridName: props.gridName,
            state: _state,
          })
        );
      }
    }
  };

  /*
    Only visible keys have been configured.
    For the full list of keys see documentation on https://www.ag-grid.com/react-data-grid/localisation, https://www.ag-grid.com/examples/localisation/localisation/locale.en.js
    */
  const localeTextFunc = (params) => {
    return t("components.datagrid.agGrid." + params.key);
  };

  const gridSizeChangeHandler = (params) => {
    if (!props.serverSideDataSource || props.fitScreen) {
      params.api.sizeColumnsToFit();
    }
  };

  const triggerRowLoad = (length) => {
    ref.current.api.ensureIndexVisible(length, "top");
  };

  return (
    <>
      <StyledGridWrapper noCardBorder={props.noCardBorder}>
        {!props.disableBasicToolBar && (
          <SFStackStyled hideGrid={props.hideGrid}>
            <SFGridContainerItem spacing={1}>
              {props.search && <SFGridItem xs={12}>{props.search}</SFGridItem>}
              <SFGridItem xs={12} md={6}>
                <SFStack justifyContent="flex-end" direction="row" spacing={1}>
                  {props.actions &&
                    props.actions.map((a) => (
                      <SFIconButton
                        type="button"
                        color="grey500"
                        onClick={a.onClick}
                        ariaLabel={a.label}
                      >
                        {React.cloneElement(a.icon, { color: "undefined" })}
                      </SFIconButton>
                    ))}
                </SFStack>
              </SFGridItem>
            </SFGridContainerItem>
          </SFStackStyled>
        )}
        {props.subBar && <SFStack borderBottom>{props.subBar}</SFStack>}
        <div
          style={{
            height: "100%",
            width: "100%",
            minHeight: 300,
            overflowY: props.hideGrid ? "scroll" : "initial",
            paddingTop: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              ...(!props.hideGrid && { height: "100%" }),
            }}
          >
            <div style={{ flexGrow: "1" }}>
              <div
                className={` ${[
                  ...(props.gridTheme ? [props.gridTheme] : ["ag-theme-ey"]),
                  ...(props.rowModelType === "clientSide"
                    ? ["ag-theme-ey-client"]
                    : []),
                  ...(props.rowModelType === "serverSide" ? ["ag-server"] : []),
                  ...(props.hideHeader ? ["ag-no-header"] : []),
                  ...(props.transparent ? ["ag-transparent"] : []),
                ].join(" ")}`}
                style={{ width: "100%", height: "100%" }}
              >
                {!loading && (
                  <AgGridReact
                    {...props}
                    groupDisplayType={
                      props.columnDefs?.filter((a) => a.enableRowGroup)
                        ? "groupRows"
                        : null
                    }
                    ref={ref}
                    defaultColDef={props.defaultColDefs ?? defaultColDefs}
                    loadingCellRendererParams={{
                      loadingMessage: t("components.datagrid.loadingMessage"),
                    }}
                    context={{
                      hideMasterDetailArrow: props.hideMasterDetailArrow, // hide master detail cell renderer expand/collapse arrow
                      masterDetail: props.masterDetail,
                      persistState: props.persistState,
                      viewDetailsUrl: props.viewDetailsUrl,
                      allowMultipleDetailRows: props.allowMultipleDetailRows,
                      filterValuesUrl: props.filterValuesUrl,
                      isRowMaster: props.isRowMaster,
                      getCopiedRowData: props.getCopiedRowData,
                      getExpandedRowId: props.getExpandedRowId,
                      setExpandedRowId: props.setExpandedRowId,
                      permissions: {
                        viewDetails:
                          props.permissions?.viewDetails ??
                          props.permissions?.view,
                        edit: props.permissions?.edit,
                        delete: props.permissions?.delete,
                      },
                      //any props to be passed in detail grid
                      detailProps: props.detailProps,
                      gridName: props.gridName,
                      //prop describing the type of entity the grid is displaying
                      entityType: props.entityType,
                    }}
                    popupParent={document.querySelector("body")}
                    getLocaleextFunc={localeTextFunc}
                    onStateUpdated={onStateUpdated}
                    components={props.customRenderers}
                    onGridSizeChanged={gridSizeChangeHandler}
                    columnDefs={props.columnDefs}
                    onGridReady={handleGridReady}
                    suppressPropertyNamesCheck
                    suppressDragLeaveHidesColumns
                    suppressRowClickSelection={
                      props.suppressRowClickSelection ?? true
                    }
                    rowData={
                      props.rowModelType === "clientSide"
                        ? props.rowData
                        : undefined
                    }
                    rowSelection={
                      props.hasRowSelection ? "multiple" : undefined
                    }
                    rowDragManaged={props.rowDragging}
                    //enable this at your own risk :)
                    animateRows={false}
                  />
                )}
              </div>
            </div>
          </div>
          {props.hideGrid && (
            <RowContainer
              row={props.rowComponent}
              data={props.customRowData}
              triggerLoad={triggerRowLoad}
              loadingMessage={t("components.datagrid.loadingMessage")}
            />
          )}
        </div>
      </StyledGridWrapper>
    </>
  );
});

export default SFDataGridCore;

function combineFilterState(preconfiguredState, savedLayoutState) {
  let newState = _.cloneDeep(savedLayoutState);
  //initialize state with empty filter when savedlayout doesnt exist
  if (!newState?.filter) {
    newState = {
      filter: {
        filterModel: {},
      },
    };
  }
  if (preconfiguredState?.filter?.filterModel) {
    //replace filters of the saved layout with the ones passed in the props
    Object.keys(preconfiguredState?.filter?.filterModel).forEach((key) => {
      newState.filter.filterModel[key] =
        preconfiguredState?.filter?.filterModel[key];
    });
  }
  //initialize state with empty filter when preconfiguredState doesnt exist
  else newState.filter.filterModel = {};
  return newState?.filter?.filterModel ?? {};
}
